import classnames from 'classnames/bind'
import { useRef, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'
import { FACETS_TO_RETRIEVE } from '~/lib/constants'

import { useAlgoliaSearch } from '@unlikelystudio/react-ecommerce-hooks'

import { usePopIn } from '~/providers/PopInProvider'
import { useStyle } from '~/providers/StyleProvider'

import useLocale from '~/hooks/useLocale'

import {
  formDataObjectToAlgoliaFilters,
  formDataToObject,
} from '~/utils/form-data'
import getAlgoliaProductIndex from '~/utils/get-algolia-product-index'

import { CTA, FILTER } from '~/data/dictionary'

import Accordion from '../Abstracts/Accordion'
import Tag from '../Abstracts/Tag'
import AccordionPanelUi from '../AccordionPanelUi'
import Checkbox from '../Form/Checkbox'
import Switcher from '../Switcher'
import BlurredCta from '../UI/BlurredCta'
import PopIn from '../UI/PopIn'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export type FacetFilters = Array<string | string[]>
export interface PopInFiltersProps {
  className?: string
  facets: ReturnType<typeof useAlgoliaSearch>['data']['pages'][0]['facets']
  defaultFacetFilters: FacetFilters
  hideFacets?: string[]
  onSubmit?: (facets: FacetFilters, index: string) => void
  defaultIndex?: string
}

function PopInFilters({
  className,
  facets = {},
  onSubmit,
  defaultFacetFilters,
  defaultIndex,
}: PopInFiltersProps) {
  const noFiltersTextStyle = useStyle({
    textPreset: GlobalTextPreset?.Text12Secondary,
  })
  const [index, setIndex] = useState(defaultIndex)
  const t = useTranslate()
  const { removeCurrent } = usePopIn()
  const [isReseted, setIsReseted] = useState(false)
  const [facetFilters, setFacetFilters] = useState(defaultFacetFilters)
  const checkedCount =
    facetFilters?.flat()?.filter((facet) => {
      const [key] = facet.split(':')
      return FACETS_TO_RETRIEVE.includes(key)
    }).length ?? 0
  const formRef = useRef()

  const flattenFacetsFilters = isReseted
    ? []
    : typeof defaultFacetFilters === 'string'
    ? [defaultFacetFilters]
    : defaultFacetFilters?.flat()

  const locale = useLocale()
  const baseAlgoliaIndex = `${locale}_${getAlgoliaProductIndex()}`
  const indexes = [baseAlgoliaIndex, `${baseAlgoliaIndex}_price_asc`]

  const switcherIndex = indexes?.findIndex((currIndex) => index === currIndex)
  const updateFacets = () => {
    const data = new FormData(formRef.current)
    setFacetFilters(formDataObjectToAlgoliaFilters(formDataToObject(data)))
  }

  const filteredFacets = Object.keys(facets).filter((facet) => {
    return Object.keys(facets[facet])?.length > 1
  })

  const hasFacetFilters = filteredFacets?.length > 0
  const withoutFacetFilters = !hasFacetFilters

  return (
    <PopIn
      popinHeader={t(FILTER.REFINE)}
      className={cx(css.PopInFilters, className, {
        withoutFacetFilters: !hasFacetFilters,
      })}>
      <form
        ref={formRef}
        id="filters"
        className={css.form}
        onSubmit={(e) => {
          e.preventDefault()
          const data = new FormData(e.currentTarget)

          onSubmit?.(
            formDataObjectToAlgoliaFilters(formDataToObject(data)),
            index,
          )
          removeCurrent()
        }}>
        <Switcher
          className={css.switcher}
          switcherItemClassName={css.switcherItem}
          currentIndex={switcherIndex}
          switchers={[
            {
              onClick: () => {
                setIndex(indexes[0])
              },
              children: t(FILTER.ORDER_BY_NEWEST),
            },
            {
              onClick: () => {
                setIndex(indexes[1])
              },
              children: t(FILTER.ORDER_BY_PRICE_ASC),
            },
          ]}
        />
        {/* <Checkbox
          className={css.expressDelivery}
          defaultChecked={flattenFacetsFilters.includes(
            `${FACETS.EXPRESS_DELIVERY}:true`,
          )}
          name={FACETS.EXPRESS_DELIVERY}
          value={`${FACETS.EXPRESS_DELIVERY}:true`}
          label="Livraison express"
          key={FACETS.EXPRESS_DELIVERY}
        /> */}
        {!hasFacetFilters && (
          <span className={cx(css.noFilters, noFiltersTextStyle)}>
            {t(FILTER.NO_FILTERS)}
          </span>
        )}
        {filteredFacets?.map((facetName) => {
          const facetIsUsed = !!flattenFacetsFilters?.find((facet) => {
            const [key] = facet.split(':')
            return key === facetName
          })
          return (
            <Accordion
              index={facetIsUsed ? 0 : undefined}
              key={facetName}
              className={cx(
                css.selectContainer,
                css.customizationSelectContainer,
              )}>
              <AccordionPanelUi
                title={t(`facet.${facetName}`)}
                body={
                  <div>
                    {Object.entries(facets[facetName])?.map(
                      ([key, facetValue]) => {
                        const isChecked = !!flattenFacetsFilters?.find(
                          (facetFilter) => {
                            const [key, value] = facetFilter?.split(':')
                            return key === facetName && value === key
                          },
                        )

                        return (
                          <Checkbox
                            onClick={updateFacets}
                            className={css.option}
                            defaultChecked={isChecked}
                            name={facetName}
                            value={`${facetName}:${key}`}
                            label={`${key} (${facetValue})`}
                            key={`${facetName}-${key}`}
                          />
                        )
                      },
                    )}
                  </div>
                }
              />
            </Accordion>
          )
        })}
      </form>
      <div className={cx(css.bottomSection, { withoutFacetFilters })}>
        {hasFacetFilters && (
          <BlurredCta
            type="reset"
            form="filters"
            onClick={() => {
              setFacetFilters([])
              setIsReseted(true)
            }}
            className={cx(css.cta, { disabled: checkedCount === 0 })}
            isBig>
            {t(CTA.DELETE)}
            {checkedCount > 0 && (
              <Tag className={css.checkedCount} label={checkedCount + ''}></Tag>
            )}
          </BlurredCta>
        )}
        <BlurredCta
          form="filters"
          className={css.cta}
          type="submit"
          theme={GlobalThemeColors.Black}
          isBig>
          {t(CTA.VALIDATE)}
        </BlurredCta>
      </div>
    </PopIn>
  )
}

PopInFilters.defaultProps = {}

export default PopInFilters
